import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import LTIErrorState from "../views/LTIErrorState";
import * as Sentry from "@sentry/react-native";
import { useTranslation } from "react-i18next";

type LTILayoutProps = {
  children: React.ReactNode;
};

type LTILayoutErrorBoundaryState = {
  hasError: boolean;
};

function LTILayoutRenderErrorView() {
  const { t } = useTranslation();

  return (
    <LTIErrorState
      title={t("Unable to load this LTI page")}
      message={t("A rendering issue occurred in this iframe. Please refresh the page or relaunch from your LMS.")}
      recoveryTitle={t("What you can do")}
      recoveryMessage={t("Try launching this activity again from your LMS.")}
    />
  );
}

class LTILayoutErrorBoundary extends React.Component<LTILayoutProps, LTILayoutErrorBoundaryState> {
  constructor(props: LTILayoutProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): LTILayoutErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
    // Avoid throwing from the boundary; this keeps the iframe UI stable.
    if (process.env.NODE_ENV !== "production") {
      console.error("LTILayout render error", error);
    }
  }

  render() {
    if (this.state.hasError) {
      return <LTILayoutRenderErrorView />;
    }

    return this.props.children;
  }
}

function LTILayout({ children }: LTILayoutProps) {
  return (
    <View style={styles.root}>
      <ScrollView style={styles.contentArea} contentContainerStyle={styles.contentContainer}>
        <LTILayoutErrorBoundary>{children}</LTILayoutErrorBoundary>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    // Web: fill the viewport so the ScrollView can scroll within it
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flex: 1,
    width: "100%",
    minHeight: 0,
    minWidth: 0,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  contentArea: {
    flex: 1,
    width: "100%",
    minHeight: 0,
    minWidth: 0,
    backgroundColor: "#ffffff",
  },

  contentContainer: {
    flexGrow: 1,
  },
});

export default LTILayout;
